import { useAuth, useOrganization, useUser } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'

// Slim "LIVE PRODUCTION TENANT" reminder bar for Mira on EMSERVICE.
//
// Mira flips between two phone accounts: autofloapp@gmail.com (her
// GYM Auto test tenant) and mr.artemgurin@gmail.com (a member of
// Emil's real EMSERVICE shop). She's hit at least one bug where she
// thought she was in GYM Auto and created live appointments on
// Emil's calendar. The banner is the cheap, always-visible reminder.
//
// Gating: ONE specific email × ONE specific shop. Emil himself
// (emservice.main@gmail.com) doesn't need this — he has no other
// tenant to confuse it with — so the banner stays off for him.
//
// Shop_id is the canonical key (matches scripts/, alpha-features.ts);
// the query reuses the cached /api/me response keyed on
// (user_id, org_id) so this adds no extra round-trip.

const MIRA_SECONDARY_EMAIL = 'mr.artemgurin@gmail.com'
const EMSERVICE_SHOP_ID = 'shop_486716672d9c4df0b42ef0'

export function ProductionTenantBanner() {
  const { user } = useUser()
  const { organization } = useOrganization()
  const { getToken } = useAuth()

  const email = user?.primaryEmailAddress?.emailAddress ?? null
  const emailMatches = email === MIRA_SECONDARY_EMAIL

  const meQuery = useQuery<{ shop_id: string }>({
    queryKey: ['me', user?.id, organization?.id],
    enabled: emailMatches && !!user?.id && !!organization?.id,
    staleTime: Infinity,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error('no_token')
      const res = await fetch('/api/me', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('me_failed')
      return (await res.json()) as { shop_id: string }
    },
  })

  if (!emailMatches) return null
  if (meQuery.data?.shop_id !== EMSERVICE_SHOP_ID) return null

  return (
    <div
      role="status"
      className="sticky top-[57px] z-20 border-b border-red-700 bg-red-600 text-white"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-center gap-2 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide sm:px-6">
        <AlertTriangle className="size-3.5 shrink-0" aria-hidden />
        <span>Live production tenant — EMSERVICE</span>
      </div>
    </div>
  )
}
